import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { BottomSheet, Box, Button, Column, Menu, Popover, Sidebar, TextButton, Typography } from 'seagull';
import { ChevronDown, ChevronRight, PlusCircle, Settings } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useCreateVendorAccount } from '~/hooks/useCreateVendorAccount/useCreateVendorAccount';
import { useScrollBorder } from '~/hooks/useScrollBorder';
import { prevBusinessIdKey, useSwitchBusiness } from '~/hooks/useSwitchBusiness';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import styles from './BusinessSwitcher.module.scss';
import { getBusinessTriggerLogoUrl, getBusinessTriggerSubtext } from './BusinessSwitcher.utils';
import { BusinessSwitcherEdit } from './BusinessSwitcherEdit';
import { BusinessSwitcherLogo } from './BusinessSwitcherLogo';
import { useWorkspaceReorder } from './useWorkspaceReorder';
import { useWorkspaceVisibility } from './useWorkspaceVisibility';
export const getBusinessSwitcherTID = ()=>getTestIds();
export const BusinessSwitcher = (param)=>{
    let { userInfo, inSidebar } = param;
    var _anchorEl_current;
    const { BusinessSwitcherTID } = useMemo(()=>getBusinessSwitcherTID(), []);
    const { logBI, resetBusinessData, setWorksAt } = useStoreActions();
    const { switchBusiness } = useSwitchBusiness();
    const { localStorage } = useIoContext();
    const workspaces = useMemo(()=>userInfo.workspaces() || [], [
        userInfo
    ]);
    const businessId = userInfo.businessId();
    const isClient = useStore(selectors.user.isClient);
    const onCreateVendorAccount = useCreateVendorAccount(()=>logBI(18107));
    const { t: bsTexts } = useLocaleKeys('components.navigation.businessSwitcher');
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('read');
    const anchorEl = useRef(null);
    const menuListRef = useRef(null);
    const navigate = useNavigate();
    const validWorkspaces = useMemo(()=>workspaces.filter((workspace)=>workspace.isDisplayableInSwitcher()), [
        workspaces
    ]);
    const hasAVendorAccount = userInfo.hasAVendorAccount();
    const { footerClassName } = useScrollBorder({
        contentRef: menuListRef,
        enabled: open,
        footerBaseClassName: styles.footerScrollBorder
    }, [
        open,
        validWorkspaces
    ]);
    const { reorder } = useWorkspaceReorder({
        workspaces
    });
    const { setVisibility } = useWorkspaceVisibility({
        workspaces
    });
    const visibleWorkspaces = useMemo(()=>validWorkspaces.filter((workspace)=>!workspace.isHidden() || workspace.id() === businessId), [
        validWorkspaces,
        businessId
    ]);
    const canManageAccounts = validWorkspaces.length > 1;
    const editItemsList = useMemo(()=>validWorkspaces.map((workspace)=>({
                id: workspace.id(),
                name: workspace.name(),
                subtext: workspace.getBusinessSwitcherItemSubtext(bsTexts),
                logoUrl: workspace.getBusinessSwitcherItemLogoUrl(),
                hidden: workspace.isHidden(),
                canToggleHidden: workspace.id() !== businessId
            })), [
        validWorkspaces,
        businessId
    ]);
    const enterEditMode = useCallback(()=>{
        setMode('edit');
    }, []);
    const exitEditMode = useCallback(()=>{
        setMode('read');
    }, []);
    const closePopover = useCallback(()=>{
        setOpen(false);
    }, []);
    useEffect(()=>{
        if (!open && mode === 'edit') {
            setMode('read');
        }
    }, [
        open,
        mode
    ]);
    const menuItems = useMemo(()=>{
        return visibleWorkspaces.map((workspace, index)=>{
            const selected = workspace.id() === businessId;
            const menuItem = {
                id: workspace.id(),
                body: workspace.name(),
                subtext: workspace.getBusinessSwitcherItemSubtext(bsTexts),
                selected,
                testId: BusinessSwitcherTID.Item(index),
                prefixIcon: /*#__PURE__*/ _jsx(BusinessSwitcherLogo, {
                    testId: BusinessSwitcherTID.MenuItem(index),
                    src: workspace.getBusinessSwitcherItemLogoUrl()
                }),
                bi: {
                    name: 'business_switcher_select_workspace',
                    data: {
                        workspace_id: workspace.id()
                    }
                },
                onClick: ()=>{
                    if (selected) return;
                    closePopover();
                    switchBusiness(workspace.id());
                }
            };
            return menuItem;
        });
    }, [
        visibleWorkspaces,
        switchBusiness,
        BusinessSwitcherTID,
        businessId,
        closePopover
    ]);
    const footerItems = useMemo(()=>{
        return compact([
            hasAVendorAccount && {
                variant: 'contextualAction',
                props: {
                    bi: {
                        name: 'business_switcher_create_additional_account'
                    },
                    testId: BusinessSwitcherTID.ContextualAction(),
                    title: bsTexts.createAdditionalAccount(),
                    onClick: ()=>{
                        closePopover();
                        logBI(11151);
                        if (businessId) {
                            localStorage.setItem(prevBusinessIdKey, businessId);
                        }
                        resetBusinessData();
                        setWorksAt();
                        navigate(routes.vendorOnboarding);
                    },
                    icon: /*#__PURE__*/ _jsx(PlusCircle, {})
                }
            },
            !hasAVendorAccount && {
                variant: 'promotionalAction',
                props: {
                    testId: BusinessSwitcherTID.PromotionalAction(),
                    title: bsTexts.tryAnchor(),
                    body: bsTexts.tryAnchorBody(),
                    onClick: ()=>{
                        closePopover();
                        onCreateVendorAccount();
                    },
                    className: {
                        wrapper: styles.promotionalActionWrapper,
                        button: styles.promotionalActionButton
                    },
                    bi: {
                        name: 'try_anchor_for_your_business'
                    }
                }
            },
            canManageAccounts && {
                variant: 'contextualAction',
                props: {
                    bi: {
                        name: 'business_switcher_manage_accounts'
                    },
                    testId: BusinessSwitcherTID.ManageAccountsAction(),
                    title: bsTexts.manageAccounts(),
                    onClick: enterEditMode,
                    icon: /*#__PURE__*/ _jsx(Settings, {})
                }
            }
        ]);
    }, [
        hasAVendorAccount,
        onCreateVendorAccount,
        BusinessSwitcherTID,
        businessId,
        canManageAccounts,
        enterEditMode,
        closePopover
    ]);
    const toggleMenu = useCallback(()=>{
        const wasOpen = open;
        setOpen((prev)=>!prev);
        if (!wasOpen && isClient) {
            logBI(18106);
        }
    }, [
        open,
        isClient
    ]);
    const editBody = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Box, {
                className: styles.editScroll,
                children: /*#__PURE__*/ _jsx(BusinessSwitcherEdit, {
                    items: editItemsList,
                    onReorder: reorder,
                    onToggleVisibility: setVisibility,
                    testId: BusinessSwitcherTID.EditList()
                })
            }),
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.editFooter,
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.editFooterCreateRow,
                        children: /*#__PURE__*/ _jsx(TextButton, {
                            bi: {
                                name: 'business_switcher_create_additional_account_disabled'
                            },
                            underline: "none",
                            weight: "normal",
                            prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                            disabled: true,
                            children: bsTexts.createAdditionalAccount()
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        className: styles.editFooterManageRow,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                weight: "light",
                                skin: "secondary",
                                className: styles.editFooterHint,
                                children: bsTexts.manageAccountsHint()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'business_switcher_manage_accounts_done'
                                },
                                testId: BusinessSwitcherTID.DoneButton(),
                                onClick: exitEditMode,
                                size: "small",
                                skin: "primary",
                                children: bsTexts.done()
                            })
                        ]
                    })
                ]
            })
        ]
    });
    const minWidth = (_anchorEl_current = anchorEl.current) === null || _anchorEl_current === void 0 ? void 0 : _anchorEl_current.offsetWidth;
    return inSidebar ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Sidebar.Item, {
                id: "business-switcher",
                onClick: toggleMenu,
                testId: BusinessSwitcherTID.Button(),
                bi: {
                    name: 'sidebar_business_switcher'
                },
                after: /*#__PURE__*/ _jsx(ChevronRight, {
                    size: 16
                }),
                children: userInfo.businessName()
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: open,
                onClose: closePopover,
                paddingBottom: 0,
                children: mode === 'edit' ? editBody : /*#__PURE__*/ _jsx(Menu, {
                    testId: BusinessSwitcherTID.Menu(),
                    dimensions: {
                        width: 'auto'
                    },
                    items: menuItems,
                    footer: {
                        items: footerItems
                    }
                })
            })
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TextButton, {
                onClick: toggleMenu,
                testId: BusinessSwitcherTID.Button(),
                bi: {
                    name: 'open_business_switcher'
                },
                prefixIcon: /*#__PURE__*/ _jsx(BusinessSwitcherLogo, {
                    fullLogo: true,
                    testId: BusinessSwitcherTID.Button(),
                    src: getBusinessTriggerLogoUrl(userInfo.business())
                }),
                suffixIcon: /*#__PURE__*/ _jsx(Box, {
                    className: cn(styles.arrowIcon, {
                        [styles.open]: open
                    }),
                    children: /*#__PURE__*/ _jsx(ChevronDown, {})
                }),
                skin: "secondary",
                underline: "none",
                ref: anchorEl,
                children: /*#__PURE__*/ _jsxs(Column, {
                    alignItems: "start",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            ellipsis: true,
                            children: userInfo.businessName()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            ellipsis: true,
                            children: getBusinessTriggerSubtext(userInfo.business(), bsTexts)
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Popover, {
                open: open,
                onClose: closePopover,
                anchorEl: anchorEl.current,
                children: mode === 'edit' ? /*#__PURE__*/ _jsx(Box, {
                    className: cn(styles.editContainer, styles.popoverEdit),
                    style: {
                        width: Math.max(344, minWidth !== null && minWidth !== void 0 ? minWidth : 0)
                    },
                    children: editBody
                }) : /*#__PURE__*/ _jsx(Menu, {
                    testId: BusinessSwitcherTID.Menu(),
                    dimensions: {
                        width: Math.max(344, minWidth !== null && minWidth !== void 0 ? minWidth : 0)
                    },
                    classes: {
                        menuList: styles.menuList
                    },
                    items: menuItems,
                    footer: {
                        items: footerItems,
                        className: footerClassName
                    },
                    menuListRef: menuListRef
                })
            })
        ]
    });
};
export function BusinessSwitcherForSpecificUsers(param) {
    let { inSidebar } = param;
    const userInfo = useStore((state)=>state.userInfo);
    const showBusinessSwitcher = useStore(selectors.showBusinessSwitcher);
    const isViewMode = useStore(selectors.isViewMode);
    if (!userInfo || !showBusinessSwitcher || isViewMode) {
        return null;
    }
    return /*#__PURE__*/ _jsx(BusinessSwitcher, {
        userInfo: userInfo,
        inSidebar: inSidebar
    });
}
